//@flow
import { uniq, without } from "ramda";
import { Maybe } from "ramda-fantasy";

import createQuantumFrame, {
  frameMock as createQuantumFrameMock,
} from "@quantum/frame-client";

import {
  type MedidaSelecionada,
  type ArquivoMedida,
  type TipoDataBase,
  type ModoApp,
  decodeAssetKey,
  encodeAssetKey,
} from "../model";

import { tryParseJson, compressAndEncode, decompressAndDecode } from "../util";

export const createLocalStorageFrame = ({
  namespace = "@quantum/relatorio/frame/local",
}: * = {}) => {
  let selectionChangeListener: Function => any = () => null;

  const fetchUserSelection = () =>
    Maybe(localStorage.getItem(`${namespace}/selecao`))
      .chain(tryParseJson)
      .getOrElse([])
      .filter(obj => "id" in obj && "type" in obj);

  const setUserSelection = selection => {
    localStorage.setItem(`${namespace}/selecao`, JSON.stringify(selection));
  };

  return {
    ...createQuantumFrameMock(),
    salvarMedidasSelecionadas: (
      medidasSelecionadas: Array<MedidaSelecionada>
    ) => {
      const medidasCompressedInBase64 = compressAndEncode(medidasSelecionadas);

      localStorage.setItem(
        `${namespace}/medidasSelecionadas`,
        `base64${medidasCompressedInBase64}`
      );
    },
    obterMedidasSelecionadas: medidasSelecionadas => {
      const medidasCache = localStorage.getItem(
        `${namespace}/medidasSelecionadas`
      );

      if (!medidasCache) {
        return null;
      }

      const isBase64 = medidasCache.startsWith("base64");
      return isBase64
        ? decompressAndDecode(medidasCache.replace("base64", ""))
        : JSON.parse(medidasCache);
    },
    salvarArquivoMedidas: (arquivoSelecionado: ArquivoMedida) => {
      const arquivoJson = JSON.stringify(arquivoSelecionado);
      localStorage.setItem(`${namespace}/arquivoSelecionado`, arquivoJson);
    },
    obterArquivoMedidas: (medidasSelecionadas: Array<MedidaSelecionada>) => {
      const arquivoCache = localStorage.getItem(
        `${namespace}/arquivoSelecionado`
      );
      return arquivoCache ? JSON.parse(arquivoCache) : null;
    },
    salvarDataBase: (dataBase: string) => {
      localStorage.setItem(`${namespace}/dataBase`, dataBase);
    },
    obterDataBase: () => {
      return localStorage.getItem(`${namespace}/dataBase`);
    },
    salvarMedidasParaPersonalizar: medidasParaPersonalizar => {
      localStorage.setItem(
        `${namespace}/medidasParaPersonalizar`,
        JSON.stringify(medidasParaPersonalizar)
      );
    },
    obterMedidasParaPersonalizar: medidasParaPersonalizar => {
      const medidasCache = localStorage.getItem(
        `${namespace}/medidasParaPersonalizar`
      );
      return medidasCache ? JSON.parse(medidasCache) : null;
    },
    obterNomeGrupoAtivosAberto: () => null,
    salvarTipoDataBase: (tipo: TipoDataBase) =>
      localStorage.setItem(`${namespace}/tipoDataBase`, tipo),
    obterTipoDataBase: () => localStorage.getItem(`${namespace}/tipoDataBase`),
    abrirModalQtLink: () => console.log("[Modal QTLink] Abrindo..."),
    abrirModalMedidaUsuario: (
      callbackImportacao,
      callbackCrud,
      callbackExclusao
    ) => {
      console.log("[Modal Medida Usuario] Abrindo...");

      if (typeof callbackImportacao === "function") {
        const idMedidaUsuario = 35;
        setTimeout(() => {
          console.log("[Modal Medida Usuario] Callback Importacao chamada");
          callbackImportacao(idMedidaUsuario);
        }, 500);
      }

      if (typeof callbackCrud === "function") {
        setTimeout(() => {
          console.log("[Modal Medida Usuario] Callback CRUD chamada");
          callbackCrud();
        }, 1000);
      }

      if (typeof callbackExclusao === "function") {
        const idMedidaUsuario = 97;
        setTimeout(() => {
          console.log("[Modal Medida Usuario] Callback Exclusão chamada");
          callbackExclusao(idMedidaUsuario);
        }, 3000);
      }
    },
    fetchUserSelection,
    setUserSelection,
    listenForSelectionChange: callback => {
      selectionChangeListener = callback;

      return () => (selectionChangeListener = () => null);
    },
    searchAsset: () => null,
    removeItemFromSelection: item => {
      const removed = [encodeAssetKey(item)];

      setUserSelection(without(removed, fetchUserSelection()));
      selectionChangeListener({
        added: [],
        removed: removed,
        currentSelection: fetchUserSelection(),
      });
    },
    removeItemsFromSelection: items => {
      const removed = items;

      setUserSelection(without(removed, fetchUserSelection()));
      selectionChangeListener({
        added: [],
        removed: removed,
        currentSelection: fetchUserSelection(),
      });
    },
  };
};

export const createExtendedFrameApi = (frameWindow: any, modoApp: ModoApp) => ({
  ...createQuantumFrame(frameWindow),
  salvarMedidasSelecionadas: (
    medidasSelecionadas: Array<MedidaSelecionada>
  ) => {
    const medidasCompressedInBase64 = compressAndEncode(medidasSelecionadas);
    frameWindow.salvarMedidasSelecionadasConfiguracaoRelatorio(
      `base64${medidasCompressedInBase64}`,
      modoApp
    );
  },
  obterMedidasSelecionadas: (medidasSelecionadas: Array<MedidaSelecionada>) => {
    try {
      const medidasEmCache = frameWindow.obterMedidasSelecionadasConfiguracaoRelatorio(
        modoApp
      );

      const isBase64 = medidasEmCache.startsWith("base64");
      return isBase64
        ? decompressAndDecode(medidasEmCache.replace("base64", ""))
        : JSON.parse(medidasEmCache);
    } catch {
      return [];
    }
  },
  salvarArquivoMedidas: (arquivoSalvo: ?ArquivoMedida) => {
    const arquivoJson = arquivoSalvo ? JSON.stringify(arquivoSalvo) : null;
    frameWindow.salvarArquivoMedidasConfiguracaoRelatorio(arquivoJson, modoApp);
  },
  obterArquivoMedidas: (medidasSelecionadas: ArquivoMedida) => {
    try {
      const arquivoEmCache = frameWindow.obterArquivoMedidasConfiguracaoRelatorio(
        modoApp
      );
      return arquivoEmCache ? JSON.parse(arquivoEmCache) : null;
    } catch {
      return null;
    }
  },
  salvarDataBase: (dataBase: string) => {
    frameWindow.salvarDataBaseConfiguracaoRelatorio(dataBase, modoApp);
  },
  obterDataBase: () => {
    return frameWindow.obterDataBaseConfiguracaoRelatorio(modoApp);
  },
  salvarMedidasParaPersonalizar: medidasParaPersonalizar => {
    const medidasJson = JSON.stringify(medidasParaPersonalizar);
    frameWindow.salvarMedidasParaPersonalizarConfiguracaoRelatorio(
      medidasJson,
      modoApp
    );
  },
  obterMedidasParaPersonalizar: medidasParaPersonalizar => {
    try {
      const medidasEmCache = frameWindow.obterMedidasParaPersonalizarConfiguracaoRelatorio(
        modoApp
      );
      return JSON.parse(medidasEmCache);
    } catch (error) {
      console.log(
        "Erro ao obter as medidas para personalizar do cache.",
        error
      );
      return [];
    }
  },
  obterNomeGrupoAtivosAberto: () => frameWindow.obterNomeGrupoAtivosAberto(),
  salvarTipoDataBase: tipo =>
    frameWindow.salvarTipoDataBaseConfiguracaoRelatorio(tipo, modoApp),
  obterTipoDataBase: () =>
    frameWindow.obterTipoDataBaseConfiguracaoRelatorio(modoApp),
  abrirModalQtLink: () => frameWindow.abrirModalQtLink(),
  abrirModalMedidaUsuario: (
    callbackImportacao,
    callbackCrud,
    callbackExclusao
  ) =>
    frameWindow.abrirModalMedidaUsuario(
      false,
      false,
      callbackImportacao,
      callbackCrud,
      callbackExclusao
    ),
});

// TODO: mover isso para o @quantum/frame
export const mockaSelecaoFrame = (frame: any): any => {
  const assets = [
    { id: 3476, type: "PGBL_VGBL" },
    { id: 411231, type: "FI" },
    { id: 355283, type: "FI" },
    { id: 222097, type: "FI" },
    { id: 392812, type: "FI" },
    { id: 95524, type: "FI" },
    { id: 379093, type: "FI" },
    { id: 143677, type: "FI" },
    { id: 271195, type: "FI" },
    { id: 312274, type: "FI" },
    { id: 417297, type: "FI" },
    { id: 160253, type: "FI" },
    { id: 411231, type: "FI" },
    { id: 171786, type: "FI" },
    { id: 125555, type: "FIDC" },
    { id: 126349, type: "FIDC" },
    { id: 130702, type: "FIDC" },
    { id: 30068675, type: "FIDC" },
    { id: 171190, type: "FI" },
    { id: 758, type: "ACAO" },
    { id: 1082, type: "EMPRESA_ABERTA" },
    { id: 14671, type: "EMPRESA_FECHADA" },
  ]
    .map(({ id, type }) => ({ id: String(id), type }))
    .map(encodeAssetKey);

  let listener: Function => any = () => null;

  let currentSelection = () => frame.fetchUserSelection().map(encodeAssetKey);

  // Ferramenta para desenvolvimento:
  // comentar linha acima e descomentar linhas abaixo para selecionar ativos a escolha.
  // let currentSelection = () =>
  //   [
  //     // Insira objetos aqui
  //   ].map(encodeAssetKey);

  const setCurrentSelection = selection =>
    frame.setUserSelection(selection.map(decodeAssetKey));

  return {
    ...frame,

    fetchUserSelection: () => currentSelection().map(decodeAssetKey),

    listenForSelectionChange: fn => {
      listener = fn;
      listener({
        added: [],
        removed: [],
        currentSelection: currentSelection().map(decodeAssetKey),
      });
      return () => (listener = () => null);
    },

    searchAsset: () => {
      const added = [assets[Math.floor(Math.random() * 100) % assets.length]];
      const selection = uniq([...currentSelection(), ...added]);
      setCurrentSelection(selection);

      listener({
        added: added.map(decodeAssetKey),
        removed: [],
        currentSelection: selection.map(decodeAssetKey),
      });
    },

    removeItemFromSelection: item => {
      console.log("excluirAtivosDaSelecao", [item]);
      const removed = [encodeAssetKey(item)];
      setCurrentSelection(without(removed, currentSelection()));
      listener({
        added: [],
        removed: removed.map(decodeAssetKey),
        currentSelection: currentSelection().map(decodeAssetKey),
      });
    },

    removeItemsFromSelection: items => {
      console.log("excluirAtivosDaSelecao", items);
      const removed = items.map(encodeAssetKey);
      setCurrentSelection(without(removed, currentSelection()));
      listener({
        added: [],
        removed: removed.map(decodeAssetKey),
        currentSelection: currentSelection().map(decodeAssetKey),
      });
    },

    addAsset: ({ id, type }) => {
      const asset = encodeAssetKey({ id, type });
      const assets = currentSelection();
      if (!assets.includes(asset)) {
        setCurrentSelection([...assets, asset]);
        listener({
          added: [asset].map(decodeAssetKey),
          removed: [],
          currentSelection: assets.map(decodeAssetKey),
        });
      }
    },
  };
};
